package com.neurobs.ccabserver.administrator.view

import com.neurobs.ccab.SequenceStatus
import com.neurobs.ccabserver.administrator.controller.Session
import com.neurobs.ccabserver.administrator.controller.TestStationAdapter
import com.neurobs.js.dom.ElementFactory.{PanelView, makeButton, makeSelectBox}
import com.neurobs.js.dom.{ButtonButton, SelectSelectBox}
import com.neurobs.js.view.{Container, Gui}
import scalatags.JsDom.all._
import com.neurobs.js.dom.Grid._

/** The main control panel with current status and pause/quit/run buttons */
class SessionControlView {
	import com.neurobs.js.dom.DOMViewElement._
	import SessionControlView._

	private val panel = new PanelView(title = "", fullWidth = true, showRefresh = false, useHeader = false, styles = Some(List(("color", "#FFFFFF"), ("min-width", "40em"))))

	val container: Container = panel.container

	val rerunStartSelect: SelectSelectBox = makeSelectBox(width = 10)
	val quitTestButton: ButtonButton = makeButton(caption = "Quit Test")
	val resumeButton: ButtonButton = makeButton(caption = "Resume")
	val pauseButton: ButtonButton = makeButton(caption = "Pause")
	val reRunTestButton: ButtonButton = makeButton(caption = "Rerun")

	def update(status: SequenceStatus.Value, isChatting: Boolean, current: Option[CurrentTest], nextTestName: Option[String]): Unit = {
		panel.grid.clear()
		current.foreach { test =>
			val state = status match {
				case SequenceStatus.Running => "running"
				case SequenceStatus.Paused => "paused"
				case _ => if (test.error.isDefined) "aborted" else "completed"
			}
			val heading = s"${test.name} $state"
			panel.grid.addRow(div(style := "text-align:center;font-size:120%", heading).render)
			if ((status == SequenceStatus.Stopped) || (status == SequenceStatus.Finished)) {
				reRunTestButton.setCaption(s"Rerun ${test.name}")
				if (test.startPositions.isEmpty) panel.grid.addRow(reRunTestButton.button)
				else {
					val start = test.startPosition.flatMap { test.startPositions.indexOf(_) match {
							case -1 => None
							case i => Some(i)
						}
					}
					rerunStartSelect.setOptions(test.startPositions, start)
					panel.grid.addRow(span(reRunTestButton.button, " Start position: ", rerunStartSelect.select).render)
				}
			}
		}
		if ((status == SequenceStatus.Running) || (status == SequenceStatus.Paused)) {
			resumeButton.setCaption("Resume")
			val leftButton = if (status == SequenceStatus.Running) pauseButton else resumeButton
			panel.grid.addRow(div(style := "text-align:center", leftButton.button, span(style := "margin-left:4em").render, quitTestButton.button).render)
		}
		nextTestName.foreach { name =>
			status match {
				case SequenceStatus.Running | SequenceStatus.Paused =>
					panel.grid.addRow(span(s"Next: $name").render)
				case SequenceStatus.Stopped =>
					resumeButton.setCaption(s"Next: Run $name")
					panel.grid.addRow(resumeButton.button)
				case _ =>
			}
		}
		if (status == SequenceStatus.Finished) {
			panel.grid.addRow(span("Finished").render)
		}
		val bgcolor = status match {
			case SequenceStatus.Finished => "Red"
			case SequenceStatus.Paused => "Orange"
			case SequenceStatus.Running => "Green"
			case SequenceStatus.Stopped => "Red"
		}
		panel.container.style.setProperty(propertyName = "background-color", bgcolor)

		resumeButton.setEnabled(!isChatting)
		reRunTestButton.setEnabled(!isChatting)
	}
}

object SessionControlView {
	case class CurrentTest(
		name: String,
		startPosition: Option[String],
		startPositions: Seq[String],
		error: Option[String]
	)
}

/** Managed updating and event handlers for the main control panel */
class SessionControlController (testStation: TestStationAdapter, view: SessionControlView, gui: Gui) {
	view.quitTestButton.setHandler { () =>
		gui.showConfirmationDialog(title = "Quit Confirmation", yesNoQuestion = "Do you want to quit the current test?") { () =>
			testStation.quitTest
		}
	}
	view.resumeButton.setHandler(() => testStation.resumeSequence)
	view.pauseButton.setHandler(() => testStation.pauseSequence)

	def update(session: Session, isChatting: Boolean): Unit = {
		val current = session.state.last_log.map { r =>
			val test = session.data.group.tests(r.run.test)
			view.reRunTestButton.setHandler { () =>
				val start = if (test.starts.isEmpty) None else view.rerunStartSelect.value
				testStation.rerunTest(r, start, updateSequencePosition = false)
			}
			SessionControlView.CurrentTest(
				name = test.name,
				startPosition = r.start,
				startPositions = test.starts,
				error = r.run.error
			)
		}
		val nextTestName = session.state.seq.nextTest(session.data.group).map(_.name)
		view.update(status = session.state.sequenceStatus, isChatting = isChatting, current = current, nextTestName = nextTestName)
	}
}